/**
 * The bell-fist art (id bell-fist; display name from the game data), hand-made in the sample-page style (replaces the
 * C4 generator skeleton). The art's own look: a temple-bronze body art — a bronze sheen running up the caster's body
 * (the vajra body), knuckle prints of a closed fist, struck-bronze chime arcs, a hollow bronze bell with bosses and
 * bands, prayer beads, ground cracks with a glowing seam, flung rock, and stepped terraces for the big palm. Ramps: trim
 * (bronze), gold (highlights), brown (earth); fire only in the lion's mane. No rays and no rising buff rings (that is
 * founder-sabre's fm-*), no stone tablet (hum-palm's pp-*). Each move is staged anticipation -> strike -> impact ->
 * dissipation; body-moving moves leave split-body afterimages. Original designs.
 */
import { add, crescent, diamond, hash, isoRing, lerp, line, px, type G, type V } from '../kit';
import type { Scene } from '../skills';
import { C, clamp01, fade, hitStar, isoDisc, level, puffs, ringOn, since, strikeAt, tilesPx, type ArtFx } from '../families/swordKit';

// ── The art's pieces ──

const angOf = (d: V): number => Math.atan2(d[1], d[0]);
const OUT = (): string => C('brown', 1);

/** A shape's cells (tone or none), built once per size and kept: drawn as row runs, one fill per run. */
interface Shape {
  x0: number;
  y0: number;
  w: number;
  h: number;
  col: (string | undefined)[];
}
const SHAPES = new Map<string, Shape>();
function cached(key: string, x0: number, y0: number, x1: number, y1: number, paint: (x: number, y: number) => string | undefined): Shape {
  let sh = SHAPES.get(key);
  if (sh) return sh;
  if (SHAPES.size > 96) SHAPES.clear();
  const w = x1 - x0 + 1;
  const h = y1 - y0 + 1;
  const col: (string | undefined)[] = new Array<string | undefined>(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) col[y * w + x] = paint(x0 + x, y0 + y);
  sh = { x0, y0, w, h, col };
  SHAPES.set(key, sh);
  return sh;
}
function blit(g: G, sh: Shape, c: V): void {
  const cx = Math.round(c[0]) + sh.x0;
  const cy = Math.round(c[1]) + sh.y0;
  for (let y = 0; y < sh.h; y++) {
    const row = y * sh.w;
    let x = 0;
    while (x < sh.w) {
      const t = sh.col[row + x];
      if (!t) {
        x++;
        continue;
      }
      let e = x + 1;
      while (e < sh.w && sh.col[row + e] === t) e++;
      g.fillStyle = t;
      g.fillRect(cx + x, cy + y, e - x, 1);
      x = e;
    }
  }
}

/** Inside a closed fist seen knuckles-on (unit space: u across, v down; knuckle row at v -0.55, thumb across v 0.3). */
function inFist(u: number, v: number): boolean {
  if (Math.abs(u) <= 0.82 && v >= -0.5 && v <= 0.72) {
    // Rounded lower corners.
    const cx = Math.abs(u) - 0.55;
    const cy = v - 0.45;
    if (cx > 0 && cy > 0 && cx * cx + cy * cy > 0.0729) return false;
    return true;
  }
  for (let i = 0; i < 4; i++) {
    const kx = -0.6 + i * 0.4;
    const dx = u - kx;
    const dy = v + 0.5;
    if (dx * dx + dy * dy <= 0.05) return true;
  }
  return false;
}

/**
 * A knuckle print of a closed fist at `c`, `s` cells half-wide: four knuckles on top, finger creases, the thumb folded
 * across. Lit from the upper right; a dark outline; `hollow`: the rim only (the print carried on).
 */
function fistPrint(g: G, c: V, s: number, hollow: boolean, lvl: number, hot = false): void {
  if (lvl <= 0 || s < 2) return;
  const hx = Math.ceil(s * 0.95) + 1;
  const sh = cached(`f${s}|${+hollow}|${+hot}`, -hx, Math.floor(-s * 0.85) - 1, hx, Math.ceil(s * 0.8) + 1, (x, y) => {
    const at = (X: number, Y: number): boolean => inFist(X / s, Y / s);
    if (!at(x, y)) return undefined;
    const edge = !at(x + 1, y) || !at(x - 1, y) || !at(x, y + 1) || !at(x, y - 1);
    if (hollow) return edge ? (hot ? C('white', 8) : C('gold', 5)) : undefined;
    if (edge) return OUT();
    const u = x / s;
    const v = y / s;
    // Finger creases between the knuckles (down to the thumb), the thumb's top line.
    const crease = v < 0.18 && Math.abs(((u + 0.8) % 0.4) - 0.2) > 0.17 && Math.abs(u) < 0.72;
    const thumb = Math.abs(v - 0.22) < 0.5 / s + 0.02 && u > -0.7 && u < 0.4;
    const lit = u - v * 0.8;
    return crease || thumb ? C('trim', 1) : hot && lit > 0.2 ? C('white', 8) : lit > 0.35 ? C('gold', 6) : lit > -0.4 ? C('trim', 4) : C('trim', 2);
  });
  level(g, lvl);
  blit(g, sh, c);
  g.globalAlpha = 1;
}

const FINGER_TOPS = [-0.95, -1.2, -1.25, -1.05] as const;

/** Inside an upright palm with the fingers held together (unit: u across, v down; fingertips to -1.25, heel to 0.85). */
function inPalmUp(u: number, v: number): boolean {
  const pu = u / 0.62;
  const pv = (v - 0.3) / 0.58;
  if (pu * pu + pv * pv <= 1) return true;
  if (Math.abs(u) <= 0.58 && v <= 0.3) {
    // Four fingers together: a rounded top, the middle ones longest.
    const i = Math.min(3, Math.max(0, Math.floor((u + 0.58) / 0.29)));
    const fx = -0.435 + i * 0.29;
    const t = FINGER_TOPS[i]!;
    if (v >= t + 0.14) return true;
    const dv = v - (t + 0.14);
    return (u - fx) * (u - fx) + dv * dv <= 0.021;
  }
  // The thumb, held in against the side.
  const ax = 0.5;
  const ay = 0.45;
  const bx = 0.78;
  const by = -0.1;
  const k = clamp01(((u - ax) * (bx - ax) + (v - ay) * (by - ay)) / ((bx - ax) ** 2 + (by - ay) ** 2));
  const qx = u - (ax + (bx - ax) * k);
  const qy = v - (ay + (by - ay) * k);
  return qx * qx + qy * qy <= 0.02;
}

/**
 * A bronze palm (fingers together, upright) at `c` (the heel's middle at c + s*0.3), `s` cells half-wide, squashed by
 * `sy`, the thumb toward `side`. Solid: outline, lit right, a small wheel mark in the middle; `hollow`: a gold rim only.
 */
function bronzePalm(g: G, c: V, s: number, side: number, sy: number, hollow: boolean, lvl: number): void {
  if (lvl <= 0 || s < 2) return;
  const hx = Math.ceil(s * 0.85) + 1;
  const mark = s >= 7;
  const sh = cached(`p${s}|${sy}|${side}|${+hollow}`, -hx, Math.floor(-s * sy * 1.3) - 1, hx, Math.ceil(s * sy * 0.9) + 1, (x, y) => {
    const at = (X: number, Y: number): boolean => inPalmUp((X / s) * side, Y / (s * sy));
    if (!at(x, y)) return undefined;
    const edge = !at(x + 1, y) || !at(x - 1, y) || !at(x, y + 1) || !at(x, y - 1);
    if (hollow) return edge ? C('gold', 5) : undefined;
    if (edge) return OUT();
    const u = (x / s) * side;
    const v = y / (s * sy);
    // The gaps between the fingers.
    const gap = v < -0.62 && Math.abs(((u + 0.58) % 0.29) - 0.145) > 0.125 && Math.abs(u) < 0.5;
    // The wheel mark in the palm.
    const r = Math.hypot(u, (v - 0.28) * 1.2);
    const wheel = mark && (Math.abs(r - 0.24) < 0.06 || r < 0.06);
    const lit = x / s - v * 0.7;
    return gap ? C('trim', 1) : wheel ? C('fire', 4) : lit > 0.45 ? C('gold', 6) : lit > -0.25 ? C('gold', 4) : C('trim', 3);
  });
  level(g, lvl);
  blit(g, sh, c);
  g.globalAlpha = 1;
}

/**
 * A hollow bronze bell with its bottom rim at `c`, `h` cells tall: a crown loop, two raised bands, rows of bosses
 * between them, a flared lip. The body filled at dither `fill` (0: none), the outline and details at `lvl`; `flash`
 * whitens the right (lit) edge.
 */
function bronzeBell(g: G, c: V, h: number, fill: number, lvl: number, flash = false, sheen = -1): void {
  if (lvl <= 0) return;
  const cx = Math.round(c[0]);
  const by = Math.round(c[1]);
  const top = by - Math.round(h);
  const half = (y: number): number => {
    const v = clamp01((y - top) / h);
    return h * (0.25 + 0.08 * v + 0.16 * v ** 5);
  };
  // The crown loop.
  level(g, lvl);
  const lr = Math.max(2, Math.round(h * 0.08));
  for (let a = 0; a < 24; a++) {
    const t = (a / 24) * Math.PI * 2;
    if (Math.sin(t) > 0.3) continue;
    px(g, cx + Math.cos(t) * lr, top - 1 + Math.sin(t) * lr * 1.2, Math.cos(t) > 0 ? C('gold', 5) : C('trim', 3));
  }
  const band1 = top + Math.round(h * 0.22);
  const band2 = top + Math.round(h * 0.72);
  for (let y = top; y <= by; y++) {
    const w = Math.round(half(y));
    const lip = y >= by - 1;
    const isBand = y === band1 || y === band2 || y === band2 + 1;
    for (let x = -w; x <= w; x++) {
      const edge = x === -w || x === w || y === top;
      const sh = sheen >= 0 && Math.abs(y - sheen) < 1.5 && x > -w + 1;
      if (edge || lip) {
        level(g, lvl);
        px(g, cx + x, y, flash && x > 0 ? C('white', 8) : x > 0 ? C('gold', 5) : x === -w ? C('trim', 2) : C('trim', 4));
        continue;
      }
      if (isBand || sh) {
        level(g, Math.min(lvl, 3));
        px(g, cx + x, y, sh ? C('gold', 6) : x > w * 0.3 ? C('gold', 5) : C('trim', 4));
        continue;
      }
      // Bosses: a 3 x 3 grid between the bands, on the lit half larger.
      const inBoss = y > band1 + 1 && y < band2 - 1 && h >= 24;
      if (inBoss) {
        const rowH = (band2 - band1) / 3;
        const ry = (y - band1 - rowH / 2) % rowH;
        const colW = (w * 2) / 5;
        const rx = (x + w) % colW;
        if (Math.abs(ry) < 1 && Math.abs(rx - colW / 2) < 1 && Math.abs(x) < w - 2) {
          level(g, lvl);
          px(g, cx + x, y, x > 0 ? C('gold', 6) : C('trim', 4));
          continue;
        }
      }
      if (fill > 0) {
        level(g, fill);
        px(g, cx + x, y, x > w * 0.4 ? C('gold', 4) : C('trim', 3));
      }
    }
  }
  g.globalAlpha = 1;
}

/** Struck-bronze chime arcs: `n` paired arcs either side of `c` (radius r, gap apart), the inner one white. */
function chime(g: G, c: V, r: number, n: number, gap: number, lvl: number, sy = 1): void {
  if (lvl <= 0) return;
  level(g, lvl);
  for (let k = 0; k < n; k++) {
    const rr = r + k * gap;
    const core = k === 0 ? C('white', 8) : C('gold', 6);
    const edge = C('trim', 4);
    crescent(g, c[0], c[1], rr, -0.55, 0.55, k === 0 ? 2 : 1, core, edge, sy, false);
    crescent(g, c[0], c[1], rr, Math.PI - 0.55, Math.PI + 0.55, k === 0 ? 2 : 1, core, edge, sy, false);
  }
  g.globalAlpha = 1;
}

/** Ground cracks from `c`: `n` jagged lines out to `len` (grown over the first frames), dark with a glowing seam. */
function cracks(g: G, c: V, n: number, len: number, seed: number, e: number, lvl: number, rot = 0): void {
  if (e < 0 || lvl <= 0) return;
  const grow = Math.min(1, (e + 1) / 3);
  level(g, lvl);
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * Math.PI * 2 + (hash(i, seed) - 0.5) * 0.6;
    const L = len * (0.6 + hash(i, seed + 1) * 0.5) * grow;
    let p: V = c;
    for (let k = 1; k <= 4; k++) {
      const r = (L * k) / 4;
      const j = (hash(i * 7 + k, seed + 2) - 0.5) * 0.7;
      const q: V = [c[0] + Math.cos(a + j) * r, c[1] + Math.sin(a + j) * r * 0.5];
      line(g, p, q, C('brown', 0));
      if (e < 6) line(g, [p[0], p[1] - 1], [q[0], q[1] - 1], e < 3 ? C('gold', 6) : C('trim', 4));
      p = q;
    }
  }
  g.globalAlpha = 1;
}

/** Rock chunks flung up from `c` and falling back (resting on the ground once down), `e` frames since the burst. */
function rocks(g: G, c: V, e: number, n: number, seed: number, sp: number, lift: number, life: number): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.6, life * 0.85));
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + hash(i, seed) * 0.7;
    const v = sp * (0.6 + hash(i, seed + 1) * 0.7);
    const gx = c[0] + Math.cos(a) * v * Math.min(e, 6);
    const gy = c[1] + Math.sin(a) * v * 0.5 * Math.min(e, 6);
    const up = lift * (0.6 + hash(i, seed + 2) * 0.6);
    const hgt = Math.max(0, up * e - 0.9 * e * e);
    const x = Math.round(gx);
    const y = Math.round(gy - hgt);
    const big = i % 3 === 0;
    g.fillStyle = OUT();
    g.fillRect(x - 1, y - 1, big ? 5 : 4, big ? 4 : 3);
    g.fillStyle = C('brown', 4);
    g.fillRect(x, y, big ? 3 : 2, big ? 2 : 1);
    px(g, x + (big ? 2 : 1), y, C('brown', 6));
  }
  g.globalAlpha = 1;
}

/** Prayer beads round an iso ring at `c` (rx, rx * sy), lifted `lift`; only the back half (`front` false) or the front. */
function beads(g: G, c: V, rx: number, n: number, rot: number, lvl: number, front: boolean, lift = 0, sy = 0.5): void {
  if (lvl <= 0) return;
  level(g, lvl);
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * Math.PI * 2;
    const sn = Math.sin(a);
    if (sn >= 0 !== front) continue;
    const x = c[0] + Math.cos(a) * rx;
    const y = c[1] + sn * rx * sy - lift;
    if (i % 9 === 0) diamond(g, x, y, 2, C('fire', 3), C('gold', 6));
    else diamond(g, x, y, 1, front ? C('trim', 4) : C('trim', 3), front ? C('gold', 6) : C('gold', 5));
  }
  g.globalAlpha = 1;
}

/**
 * The vajra body: a bronze sheen round the body at time `t` — two 2-cell side rims of a tall oval from shin to crown, lit
 * on the right, a white band of sheen running up them (`phase` 0..1), gold motes rising off the shoulders.
 */
function vajraBody(g: G, s: Scene, t: number, lvl: number, phase: number): void {
  if (lvl <= 0) return;
  const b = s.body(t);
  const cy = b[1] - s.chest * 0.95;
  const rx = Math.max(8, Math.round(s.chest * 0.42));
  const ry = s.chest * 1.05;
  const sheenY = cy + ry - phase * ry * 2;
  level(g, lvl);
  for (let i = 0; i <= 40; i++) {
    const a = -0.95 + (i / 40) * 1.9;
    for (const sd of [-1, 1]) {
      const x = b[0] + sd * Math.cos(a) * rx;
      const y = cy + Math.sin(a) * ry;
      const hot = Math.abs(y - sheenY) < 2.5;
      px(g, x, y, hot ? C('white', 8) : sd > 0 ? C('gold', 5) : C('trim', 3));
      px(g, x - sd, y, hot ? C('gold', 6) : sd > 0 ? C('trim', 4) : C('trim', 2));
    }
  }
  for (let i = 0; i < 5; i++) {
    const k = (phase * 1.7 + i * 0.21) % 1;
    const x = b[0] + (hash(i, 501) - 0.5) * rx * 2.2;
    const y = cy - ry * 0.4 - k * 18;
    px(g, x, y, k < 0.5 ? C('gold', 6) : C('trim', 4));
  }
  g.globalAlpha = 1;
}

/** Dazed: three prayer beads and one bronze tassel bead orbiting over the foe's head. */
function dazed(g: G, s: Scene, e: number, life: number): void {
  if (e < 0 || e >= life) return;
  const head: V = [s.f[0], s.f[1] - s.chest * 1.85];
  const lv = fade(e, life - 6, life - 3);
  level(g, lv);
  for (let i = 0; i < 4; i++) {
    const a = e * 0.5 + (i * Math.PI) / 2;
    const p: V = [head[0] + Math.cos(a) * 8, head[1] + Math.sin(a) * 2.5];
    if (Math.sin(a) < 0) level(g, Math.min(lv, 2));
    else level(g, lv);
    diamond(g, p[0], p[1], i === 0 ? 2 : 1, i === 0 ? C('fire', 3) : C('trim', 4), C('gold', 6));
  }
  g.globalAlpha = 1;
}

/** A bronze footprint stamped on the ground at `c` (a sole oval, toe row toward `d`), glowing `hot` at first. */
function footprint(g: G, c: V, d: V, lvl: number, hot: boolean): void {
  if (lvl <= 0) return;
  level(g, lvl);
  const a = angOf([d[0], d[1] * 2]);
  const ca = Math.cos(a);
  const sa = Math.sin(a);
  for (let y = -4; y <= 4; y++)
    for (let x = -7; x <= 7; x++) {
      // Sole space: along (forward), across.
      const al = x * ca + y * 2 * sa;
      const ac = -x * sa + y * 2 * ca;
      const r = (al / 5.5) ** 2 + (ac / (al > 0 ? 3.4 : 2.6)) ** 2;
      if (r > 1) continue;
      px(g, c[0] + x, c[1] + y, r > 0.6 ? (hot ? C('gold', 6) : C('trim', 4)) : hot ? C('fire', 3) : C('brown', 1));
    }
  g.globalAlpha = 1;
}

/** An iso terrace step: a 2:1 rhombus ring of half-width `r` at `c`, a lit tread edge over a dark riser. */
function terrace(g: G, c: V, r: number, lvl: number, hot: boolean): void {
  if (lvl <= 0 || r < 2) return;
  level(g, lvl);
  const pts: V[] = [
    [c[0] + r, c[1]],
    [c[0], c[1] + r / 2],
    [c[0] - r, c[1]],
    [c[0], c[1] - r / 2],
  ];
  for (let i = 0; i < 4; i++) {
    const p = pts[i]!;
    const q = pts[(i + 1) % 4]!;
    line(g, [p[0], p[1] + 1], [q[0], q[1] + 1], C('brown', 2));
    line(g, [p[0], p[1] + 2], [q[0], q[1] + 2], C('brown', 1));
    line(g, p, q, hot ? C('gold', 6) : i === 3 || i === 0 ? C('trim', 5) : C('trim', 3));
  }
  g.globalAlpha = 1;
}

// ── 1. ar-strike: a bronze-sheened fist, a straight blow, a knuckle print, the foe rung like a bronze bell ──

const STRIKE_HIT = 0.45;
const strike: ArtFx = {
  id: 'arhat-strike',
  skill: 'ar-strike',
  th: 'ar-strike',
  on: true,
  note: 'ประกายทองแดงวิ่งขึ้นตามหมัดที่ง้าง -> ก้าวต่อยตรง รอยกำปั้นสี่ข้อนิ้วประทับบนเป้า -> เสียงสำริดกังวาน (โค้งคลื่นสองข้าง) ประกายทองแดงกระเด็นไปหลังเป้า -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxThrust', t0: 0.1, t1: 0.95 }],
  hits: [STRIKE_HIT],
  big: STRIKE_HIT,
  ghosts: [0.34, 0.39],
  stepIn: 6,
  pre: 0.25,
  hit: STRIKE_HIT,
  end: 1.3,
  afterimage: 'port',
  under(g, s) {
    puffs(g, s.f, since(s.t, STRIKE_HIT), 6, 4, 2, 'brown', 6, 0.2, 11);
  },
  over(g, s) {
    // Anticipation: the cocked fist turning to bronze.
    if (s.t >= 0.2 && s.t < STRIKE_HIT) {
      const { hand } = s.limbs(s.t);
      fistPrint(g, hand, s.t < 0.33 ? 3 : 4, false, 3, s.t >= 0.38);
    }
    const e = since(s.t, STRIKE_HIT);
    if (e < 0) return;
    const c = strikeAt(s);
    if (e < 2) fistPrint(g, c, 10 + e * 2, false, 3, e === 0);
    else if (e < 5) fistPrint(g, add(c, s.d, (e - 1) * 3), 12 + e, true, fade(e, 3, 4));
    if (e < 7) chime(g, c, 8 + e * 3, 2, 4, fade(e, 3, 5), 1.2);
    // Bronze sparks flung on past the foe.
    if (e >= 0 && e < 7) {
      level(g, fade(e, 3, 5));
      for (let i = 0; i < 6; i++) {
        const a = angOf(s.d) + (hash(i, 21) - 0.5) * 1.5;
        const v = 3 + hash(i, 22) * 2;
        const x = c[0] + Math.cos(a) * v * e;
        const y = c[1] + Math.sin(a) * v * e * 0.7 + 0.35 * e * e;
        diamond(g, x, y, e < 3 && i % 2 ? 1 : 0, C('trim', 4), C('gold', 6));
      }
      g.globalAlpha = 1;
    }
    hitStar(g, s, c, e, 'gold', 1);
  },
};

// ── 2. ar-palm: a palm pressed to the ground, bronze palms bursting up in a running line, a big palm at the foe ──

const PALM_PUSH = 0.45;
const PALM_LAND = 0.85;
const PALM_N = 4;
const PALM_R = Math.min(52, tilesPx(2.2));
const palm: ArtFx = {
  id: 'arhat-palm',
  skill: 'ar-palm',
  th: 'ar-palm',
  on: true,
  note: 'ประกายทองแดงที่ฝ่ามือ -> ผลักฝ่ามือลงพื้น ฝ่ามือสำริดผุดจากพื้นเป็นแถววิ่งไปหาเป้า (ทีละอัน ไม่ใช่ลำแสง) รอยร้าวตามทาง -> ฝ่ามือใหญ่ผุดใต้เป้า วงพื้นสองชั้น หินกระเด็น -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxPalm', t0: 0.1, t1: 1.2 }],
  hits: [PALM_LAND],
  big: PALM_LAND,
  ghosts: [0.36, 0.41],
  stepIn: 4,
  pre: 0.3,
  hit: PALM_LAND,
  end: 1.9,
  afterimage: 'port',
  under(g, s) {
    // The seams the palms leave, one short crack under each.
    for (let i = 0; i < PALM_N; i++) {
      const at = PALM_PUSH + ((i + 1) * (PALM_LAND - PALM_PUSH)) / (PALM_N + 1);
      const e = since(s.t, at);
      if (e < 0 || e > 14) continue;
      const p = lerp(s.a, s.f, (i + 1) / (PALM_N + 1));
      cracks(g, p, 3, 8, 30 + i, e, fade(e, 8, 12), angOf(s.d));
    }
    const e = since(s.t, PALM_LAND);
    if (e >= 0 && e < 14) {
      if (e < 2) isoDisc(g, s.f, PALM_R * 0.6, C('trim', 3), C('gold', 5), 1);
      for (let k = 0; k < 2; k++) {
        const ee = e - k * 2;
        if (ee < 0 || ee > 8) continue;
        level(g, fade(ee, 3, 6));
        isoRing(g, s.f[0], s.f[1], Math.round(PALM_R * (0.45 + 0.55 * clamp01(ee / 4))), k ? C('trim', 4) : C('gold', 6), k === 0);
      }
      g.globalAlpha = 1;
      cracks(g, s.f, 7, PALM_R * 0.7, 40, e, fade(e, 8, 12));
    }
  },
  over(g, s) {
    if (s.t >= 0.22 && s.t < PALM_PUSH + 0.05) {
      const { hand } = s.limbs(s.t);
      bronzePalm(g, add(hand, [0, -2]), s.t < 0.35 ? 3 : 4, s.d[0] >= 0 ? 1 : -1, 1, false, 3);
    }
    const side = s.d[0] >= 0 ? 1 : -1;
    // The running line: each palm bursts up out of the ground, stands two frames, sinks back.
    for (let i = 0; i < PALM_N; i++) {
      const at = PALM_PUSH + ((i + 1) * (PALM_LAND - PALM_PUSH)) / (PALM_N + 1);
      const e = since(s.t, at);
      if (e < 0 || e > 5) continue;
      const p = lerp(s.a, s.f, (i + 1) / (PALM_N + 1));
      const s0 = 5 + i;
      const rise = e < 2 ? (e + 1) * 0.5 : 1 - (e - 2) * 0.22;
      bronzePalm(g, [p[0], p[1] - s0 * 0.9 * rise], s0, side, 1, e > 3, fade(e, 3, 5));
      puffs(g, p, e, 4, 2, 2, 'brown', 5, 0.4, 50 + i);
    }
    const e = since(s.t, PALM_LAND);
    if (e < 0) return;
    // The big palm bursting up under the foe, the foe thrown on it.
    if (e < 7) {
      const rise = e < 2 ? 0.6 + e * 0.3 : 1;
      bronzePalm(g, [s.f[0] + s.d[0] * 4, s.f[1] - 13 * rise], 13, side, 1, e > 3, fade(e, 4, 6));
    }
    rocks(g, s.f, e, 8, 60, 3, 4.5, 12);
    hitStar(g, s, strikeAt(s), e, 'gold', 1.1);
  },
};

// ── 3. ar-bell: a bronze bell lowered over the body, rung once on landing, standing guard, fading to its bands ──

const BELL_LAND = 0.8;
const bellH = (s: Scene): number => Math.round(s.chest * 2.35);
const bellDraw = (g: G, s: Scene): void => {
  const h = bellH(s);
  const base: V = [s.a[0], s.a[1] + 3];
  if (s.t >= 0.15 && s.t < BELL_LAND) {
    // Lowered from high above, slowing as it comes.
    const k = clamp01((s.t - 0.15) / (BELL_LAND - 0.15));
    const drop = (1 - k) * (1 - k) * h * 1.3;
    bronzeBell(g, [base[0], base[1] - drop], h, 0, k < 0.4 ? 1 : k < 0.75 ? 2 : 3);
    return;
  }
  const e = since(s.t, BELL_LAND);
  if (e < 0 || e > 26) return;
  // Rung: the lit edge flashes, the body fills for a beat; then a sheen runs down it while the guard is held.
  const fill = e < 3 ? 2 : e < 8 ? 1 : 0;
  const lvl = e < 18 ? 3 : e < 23 ? 2 : 1;
  const sheen = e >= 4 && e < 22 ? base[1] - h + ((e - 4) * 4) % h : -1;
  bronzeBell(g, base, h, fill, lvl, e < 2, sheen);
};
const bell: ArtFx = {
  id: 'arhat-bell',
  skill: 'ar-bell',
  th: 'ar-bell',
  on: true,
  note: 'ระฆังสำริดใบใหญ่ค่อยๆ ครอบลงจากเหนือหัว -> ตกถึงพื้น ระฆังดัง (ขอบสว่าง คลื่นเสียงโค้งสองข้าง วงพื้นสั่น ฝุ่น) -> ระฆังตั้งคุ้มกาย แสงทองไล่ลงตามระฆัง ลูกประคำวนรอบฐาน -> เหลือแต่ลาย จาง',
  weapon: 'none',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.2 }],
  hits: [],
  pre: 0.1,
  hit: BELL_LAND,
  end: 2.8,
  afterimage: null,
  under(g, s) {
    const e = since(s.t, BELL_LAND);
    if (e < 0 || e > 26) return;
    if (e < 8) {
      level(g, fade(e, 3, 6));
      isoRing(g, s.a[0], s.a[1] + 2, 14 + e * 3, e < 2 ? C('gold', 6) : C('trim', 4), e < 3);
      g.globalAlpha = 1;
    }
    puffs(g, s.a, e, 8, 12, 2.5, 'brown', 7, 0.3, 70);
    beads(g, s.a, 18, 18, e * 0.12, fade(e, 18, 23), false, 2);
  },
  over(g, s) {
    bellDraw(g, s);
    const e = since(s.t, BELL_LAND);
    if (e < 0 || e > 26) return;
    // The ring: chime arcs off both sides, rung twice (on landing, then again softer).
    const h = bellH(s);
    const mid: V = [s.a[0], s.a[1] - h * 0.55];
    if (e < 6) chime(g, mid, h * 0.45 + e * 4, 3, 5, fade(e, 3, 5), 1.6);
    const e2 = e - 10;
    if (e2 >= 0 && e2 < 5) chime(g, mid, h * 0.45 + e2 * 3, 2, 5, fade(e2, 2, 4) - 1, 1.6);
    beads(g, s.a, 18, 18, e * 0.12, fade(e, 18, 23), true, 2);
  },
};

// ── 4. ar-sweep: a low spinning sweep, a bronze crescent skimming the ground all round, dust walls, earth cracked ──

const SWEEP_HIT = 0.5;
const SWEEP_R = Math.min(56, tilesPx(2.4));
const sweepBand = (g: G, s: Scene, front: boolean): void => {
  const e = since(s.t, SWEEP_HIT - 0.17);
  if (e < 0 || e > 6) return;
  // The kick's arc at ankle height: one full turn over four frames, the head bright, the tail thinning to dust.
  const head = -Math.PI / 2 + Math.min(4, e + 1) * (Math.PI / 2) * (s.d[0] >= 0 ? 1 : -1);
  const sgn = s.d[0] >= 0 ? 1 : -1;
  const r = SWEEP_R * 0.72;
  const lv = fade(e, 4, 6);
  level(g, lv);
  const n = 90;
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const a = head - sgn * u * Math.PI * 1.6;
    const sn = Math.sin(a);
    if (sn >= 0 !== front) continue;
    const rows = u < 0.15 ? 4 : u < 0.55 ? 3 : 2;
    for (let k = 0; k < rows; k++) {
      const x = s.a[0] + Math.cos(a) * (r - k);
      const y = s.a[1] - 3 + sn * (r - k) * 0.5;
      px(g, x, y, u < 0.08 ? C('white', 8) : u < 0.4 ? (k === 0 ? C('gold', 6) : C('trim', 4)) : C('brown', 5));
    }
  }
  g.globalAlpha = 1;
};
const sweep: ArtFx = {
  id: 'arhat-sweep',
  skill: 'ar-sweep',
  th: 'ar-sweep',
  on: true,
  note: 'วงดินวาดรอบเท้าบอกรัศมี -> ย่อตัวเตะกวาดรอบ เสี้ยวทองแดงเรียดพื้นหมุนหนึ่งรอบ -> พื้นร้าวเป็นวง กำแพงฝุ่นพุ่งออก หินกระเด็นต่ำ -> ฝุ่นหนักค้างที่ขอบวง (ช้าลง) -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxCleave', t0: 0.1, t1: 0.95 }],
  hits: [SWEEP_HIT],
  big: SWEEP_HIT,
  ghosts: [0.36, 0.42, 0.48],
  stepIn: 3,
  pre: 0.3,
  hit: SWEEP_HIT,
  end: 1.9,
  afterimage: 'port',
  under(g, s) {
    if (s.t >= 0.12 && s.t < SWEEP_HIT) {
      level(g, 3);
      ringOn(g, s.a, SWEEP_R, Math.PI, (s.t - 0.12) / 0.3, C('trim', 4), true);
      g.globalAlpha = 1;
    }
    sweepBand(g, s, false);
    const e = since(s.t, SWEEP_HIT);
    if (e < 0 || e > 16) return;
    // A ring of short cracks round the swept circle.
    level(g, fade(e, 8, 13));
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2 + hash(i, 80) * 0.3;
      const r0 = SWEEP_R * 0.55;
      const r1 = SWEEP_R * (0.75 + hash(i, 81) * 0.2);
      const mid = (r0 + r1) / 2 + 2;
      const p0: V = [s.a[0] + Math.cos(a) * r0, s.a[1] + Math.sin(a) * r0 * 0.5];
      const pm: V = [s.a[0] + Math.cos(a + 0.08) * mid, s.a[1] + Math.sin(a + 0.08) * mid * 0.5];
      const p1: V = [s.a[0] + Math.cos(a) * r1, s.a[1] + Math.sin(a) * r1 * 0.5];
      line(g, p0, pm, C('brown', 0));
      line(g, pm, p1, C('brown', 0));
      if (e < 4) px(g, pm[0], pm[1] - 1, C('gold', 6));
    }
    isoRing(g, s.a[0], s.a[1], SWEEP_R, C('brown', 3), e < 6);
    g.globalAlpha = 1;
    // The slow: heavy dust lying along the ring's edge.
    puffs(g, s.a, Math.min(e, 4) + (e > 10 ? 3 : 0), 14, SWEEP_R * 0.7, 2, 'brown', 8, 0, 82);
  },
  over(g, s) {
    sweepBand(g, s, true);
    const e = since(s.t, SWEEP_HIT);
    // Dust walls kicked out low, rock skimming.
    if (e >= 0 && e < 7) puffs(g, s.a, e, 12, SWEEP_R * 0.5, 4, 'brown', 7, 0.8, 83);
    rocks(g, s.a, e, 10, 84, 5, 2.2, 10);
    hitStar(g, s, strikeAt(s, 0.35), e, 'gold', 0.8);
  },
};

// ── 5. ar-break: the body turns to bronze, an overhead hammer-fist, stone peaks burst up round the foe and crumble ──

const BREAK_HIT = 0.75;
const BREAK_PEAKS = 4;
const peaks = (g: G, s: Scene, e: number, front: boolean): void => {
  if (e < 0 || e > 7) return;
  // Stone peaks jutting up round the foe (two frames up, held, then crumbling down into rock).
  for (let i = 0; i < BREAK_PEAKS; i++) {
    const a = (i / BREAK_PEAKS) * Math.PI * 2 + 0.4;
    if (Math.sin(a) >= 0 !== front) continue;
    const r = 16 + hash(i, 90) * 4;
    const b: V = [s.f[0] + Math.cos(a) * r, s.f[1] + Math.sin(a) * r * 0.5];
    const H0 = 18 + hash(i, 91) * 10;
    const H = e < 2 ? H0 * (0.55 + e * 0.45) : e < 4 ? H0 : H0 * (1 - (e - 3) * 0.22);
    const w = 6 + hash(i, 92) * 3;
    level(g, fade(e, 5, 7));
    for (let y = 0; y <= H; y++) {
      const half = Math.round(w * (1 - y / H) + 0.5 * (y % 3 === 0 ? 1 : 0));
      const yy = Math.round(b[1] - y);
      for (let x = -half; x <= half; x++) {
        const edge = x === -half || x === half || y >= H - 1;
        px(g, b[0] + x, yy, edge ? OUT() : x > 0 ? (x > half - 2 ? C('brown', 6) : C('brown', 5)) : C('brown', 3));
      }
    }
    if (e < 3) px(g, b[0] + 1, b[1] - H, C('gold', 6));
  }
  g.globalAlpha = 1;
};
const brk: ArtFx = {
  id: 'arhat-break',
  skill: 'ar-break',
  th: 'ar-break',
  on: true,
  note: 'วงทองแดงวาดรอบเท้า ร่างกายเป็นทองแดง (แสงไล่ขึ้นตามตัว) หมัดกำ (ตั้งท่านาน) -> พุ่งเข้าทุบหมัดจากบน เงาร่างแยก -> ยอดหินผุดรอบเป้าแล้วพังทลาย พื้นร้าวเป็นดาว เศษการ์ดทองแตก -> ลูกประคำวนหัว (มึน)',
  weapon: 'none',
  acts: [{ m: 'wxSlam', t0: 0.25, t1: 1.2 }],
  hits: [BREAK_HIT],
  big: BREAK_HIT,
  ghosts: [0.6, 0.66, 0.72],
  stepIn: 9,
  cast: [0.05, 0.5],
  pre: 0.55,
  hit: BREAK_HIT,
  end: 2.3,
  afterimage: 'port',
  under(g, s) {
    // The break's tell: a bronze ring drawn on round the feet over the wind-up.
    if (s.t >= 0.05 && s.t < BREAK_HIT) {
      level(g, s.t < 0.55 ? 3 : 2);
      ringOn(g, s.a, 14, -Math.PI / 2, (s.t - 0.05) / 0.4, C('trim', 4), true);
      g.globalAlpha = 1;
    }
    const e = since(s.t, BREAK_HIT);
    if (e < 0 || e > 18) return;
    if (e < 10) isoDisc(g, s.f, 20, C('brown', 2), C('brown', 1), fade(e, 4, 8));
    cracks(g, s.f, 8, 36, 93, e, fade(e, 10, 15));
    peaks(g, s, e, false);
  },
  over(g, s) {
    // Anticipation: the vajra body, the sheen running up twice; the fist turning bronze.
    if (s.t >= 0.08 && s.t < BREAK_HIT) {
      const k = (s.t - 0.08) / 0.3;
      vajraBody(g, s, s.t, s.t < 0.2 ? 2 : 3, k % 1);
      const { hand } = s.limbs(s.t);
      if (s.t >= 0.3) fistPrint(g, hand, s.t < 0.5 ? 4 : 5, false, 3, s.t >= 0.62);
    }
    const e = since(s.t, BREAK_HIT);
    if (e < 0) return;
    const c = strikeAt(s, 0.9);
    // The hammer-fist coming down on the guard: a fist print dropped from above, speed lines behind it.
    if (e < 2) {
      const p: V = [c[0], c[1] - 10 + e * 8];
      level(g, 3);
      for (let i = -2; i <= 2; i++) line(g, [p[0] + i * 4, p[1] - 22], [p[0] + i * 4, p[1] - 10 - Math.abs(i) * 2], i ? C('trim', 4) : C('gold', 6));
      g.globalAlpha = 1;
      fistPrint(g, p, 10, false, 3, e === 0);
    }
    peaks(g, s, e, true);
    // The guard shattering: gold shards flung out and falling.
    if (e >= 1 && e < 9) {
      level(g, fade(e, 5, 7));
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + hash(i, 94) * 0.5;
        const v = 2.4 + hash(i, 95) * 1.6;
        const x = c[0] + Math.cos(a) * v * e;
        const y = c[1] + Math.sin(a) * v * e * 0.7 + 0.3 * e * e;
        if (i % 2) diamond(g, x, y, 1, C('gold', 5), C('white', 8));
        else line(g, [x, y], [x + Math.cos(a) * 3, y + Math.sin(a) * 2], C('gold', 6));
      }
      g.globalAlpha = 1;
    }
    rocks(g, s.f, e - 4, 10, 96, 3.5, 3.5, 12);
    hitStar(g, s, c, e, 'gold', 1.3);
    dazed(g, s, e - 5, 16);
  },
};

// ── 6. ar-lion: a breath drawn in, the roar — a bronze-and-fire mane burst round the head, sound bands, foes dazed ──

const LION_ROAR = 0.6;
const LION_HIT = 0.7;
const LION_R = Math.min(68, tilesPx(3));
const lion: ArtFx = {
  id: 'arhat-lion',
  skill: 'ar-lion',
  th: 'ar-lion',
  on: true,
  note: 'สูดลมเข้า ประกายทองไหลเข้าหาปาก อกพองเป็นทองแดง -> คำราม แผงคอสิงห์ทอง-ไฟแตกออกรอบหัว แถบเสียงหนาสามชั้นแผ่ออกทุกทิศ วงพื้นสั่นถึงขอบ -> ศัตรูรอบตัวมึน (ลูกประคำวนหัว) -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxStomp', t0: 0.1, t1: 1.1 }],
  hits: [LION_HIT],
  big: LION_HIT,
  pre: 0.3,
  hit: LION_HIT,
  end: 2.0,
  afterimage: null,
  under(g, s) {
    if (s.t >= 0.2 && s.t < LION_ROAR) {
      level(g, 2);
      ringOn(g, s.a, LION_R, -Math.PI / 2, (s.t - 0.2) / 0.35, C('trim', 4), false);
      g.globalAlpha = 1;
    }
    const e = since(s.t, LION_ROAR);
    if (e < 0 || e > 10) return;
    for (let k = 0; k < 2; k++) {
      const ee = e - k * 2;
      if (ee < 0 || ee > 7) continue;
      level(g, fade(ee, 3, 6));
      isoRing(g, s.a[0], s.a[1], Math.round(LION_R * (0.3 + 0.7 * clamp01(ee / 4))), k ? C('trim', 4) : C('gold', 6), k === 0);
    }
    g.globalAlpha = 1;
    puffs(g, s.a, e, 10, 10, 4, 'brown', 7, 0.2, 100);
  },
  over(g, s) {
    const head: V = [s.a[0] + s.d[0] * 2, s.a[1] - s.chest * 1.55];
    // The in-breath: motes streaming in to the mouth; the chest turning bronze.
    if (s.t >= 0.15 && s.t < LION_ROAR) {
      const k = clamp01((s.t - 0.15) / (LION_ROAR - 0.15));
      level(g, 3);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + 0.3;
        const from: V = [head[0] + Math.cos(a) * 30, head[1] + Math.sin(a) * 18];
        const u = clamp01(k * 1.2 - (i % 3) * 0.08);
        const p = lerp(from, head, u * u);
        const q = lerp(from, head, Math.max(0, u * u - 0.12));
        line(g, q, p, i % 2 ? C('gold', 6) : C('trim', 4));
      }
      g.globalAlpha = 1;
      if (k > 0.4) diamond(g, s.a[0], s.a[1] - s.chest, k > 0.75 ? 3 : 2, C('trim', 4), C('gold', 6));
    }
    const e = since(s.t, LION_ROAR);
    if (e < 0) return;
    // The mane: jagged gold-and-fire locks bursting out round the head, two frames out, then thinning.
    if (e < 6) {
      level(g, fade(e, 3, 5));
      const n = 18;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const L = (e < 2 ? 9 + e * 6 : 15) * (i % 2 ? 0.7 : 1);
        const r0 = 6 + Math.min(e, 3) * 2;
        const curl = (i % 2 ? 0.25 : -0.25) * (s.d[0] >= 0 ? 1 : -1);
        const p0: V = [head[0] + Math.cos(a) * r0, head[1] + Math.sin(a) * r0 * 0.9];
        const pm: V = [head[0] + Math.cos(a + curl * 0.5) * (r0 + L * 0.55), head[1] + Math.sin(a + curl * 0.5) * (r0 + L * 0.55) * 0.9];
        const p1: V = [head[0] + Math.cos(a + curl) * (r0 + L), head[1] + Math.sin(a + curl) * (r0 + L) * 0.9];
        line(g, p0, pm, e < 2 ? C('gold', 6) : C('fire', 4));
        line(g, [p0[0] + 1, p0[1]], [pm[0] + 1, pm[1]], C('fire', 3));
        line(g, pm, p1, e < 3 ? C('fire', 5) : C('fire', 3));
        if (e < 2) px(g, p1[0], p1[1], C('white', 8));
      }
      g.globalAlpha = 1;
    }
    // The roar: three thick sound bands (upright ovals) spreading from the head on every side.
    if (e < 9)
      for (let k = 0; k < 3; k++) {
        const ee = e - k * 1.5;
        if (ee < 0 || ee > 6) continue;
        level(g, fade(ee, 3, 5));
        const rx = 12 + ee * 9;
        const ry = rx * 0.62;
        const n = Math.round(rx * 5);
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2;
          // Broken bands: gaps every few cells so they read as sound, not a ring of light.
          if (((i * 7) % 11) < 2) continue;
          const x = head[0] + Math.cos(a) * rx;
          const y = head[1] + 6 + Math.sin(a) * ry;
          px(g, x, y, k === 0 ? C('white', 8) : C('gold', 6));
          px(g, x - Math.cos(a), y - Math.sin(a), k === 0 ? C('gold', 5) : C('trim', 4));
        }
      }
    g.globalAlpha = 1;
    hitStar(g, s, strikeAt(s, 1.4), since(s.t, LION_HIT), 'gold', 0.8);
    dazed(g, s, since(s.t, LION_HIT) - 1, 12);
  },
};

// ── 7. ar-step: one huge stride — glowing bronze footprints stamped along the way, split-body ghosts, a fist on arrival ──

const STEP_GO = 0.42;
const STEP_HIT = 0.68;
const STEP_D = Math.min(96, Math.round(tilesPx(5) * 0.6));
const stepMove = (s: Scene, t: number): V => {
  const k0 = clamp01((t - STEP_GO) / 0.24);
  const k = 1 - (1 - k0) * (1 - k0);
  const back = 1 - clamp01((t - 1.9) / 0.3);
  return [s.d[0] * STEP_D * k * back, s.d[1] * STEP_D * k * back];
};
const step: ArtFx = {
  id: 'arhat-step',
  skill: 'ar-step',
  th: 'ar-step',
  on: true,
  note: 'ย่อตัว ร่างเป็นทองแดง รอยเท้าแรกประทับร้าว -> ก้าวเดียวพุ่งถึงเป้า เงาร่างแยก รอยเท้าทองแดงเรืองประทับตามทาง -> ต่อยหมัดถึงตัว รอยกำปั้น เสียงสำริด พื้นใต้เป้าร้าว -> รอยเท้าเย็นลงแล้วจาง',
  weapon: 'none',
  acts: [{ m: 'wxThrust', t0: 0.3, t1: 1.2 }],
  hits: [STEP_HIT],
  big: STEP_HIT,
  ghosts: [0.5, 0.56, 0.62],
  pre: 0.35,
  hit: STEP_HIT,
  end: 1.9,
  dash: STEP_D,
  afterimage: 'dash',
  move: stepMove,
  under(g, s) {
    puffs(g, s.a, since(s.t, STEP_GO - 0.02), 7, 3, 2.5, 'brown', 7, 0.4, 110);
    // Footprints, alternating sides, stamped as the body passes (hot first, cooling to dark).
    const side: V = [-s.d[1], s.d[0]];
    for (let i = 0; i < 3; i++) {
      const at = STEP_GO + i * 0.08;
      const e = since(s.t, at);
      if (e < 0 || e > 18) continue;
      const p = add(add(s.a, s.d, 6 + (i * STEP_D) / 3), side, i % 2 ? 4 : -4);
      footprint(g, p, s.d, fade(e, 10, 15), e < 5);
      if (e < 6) cracks(g, p, 4, 9, 111 + i, e, fade(e, 3, 5));
    }
    const e = since(s.t, STEP_HIT);
    if (e >= 0) cracks(g, s.f, 6, 20, 115, e, fade(e, 8, 12));
  },
  over(g, s) {
    if (s.t >= 0.25 && s.t < STEP_HIT + 0.05) vajraBody(g, s, s.t, s.t < 0.36 ? 2 : 3, clamp01((s.t - 0.25) / 0.45));
    const e = since(s.t, STEP_HIT);
    if (e < 0) return;
    const c = strikeAt(s);
    if (e < 2) fistPrint(g, c, 8 + e * 2, false, 3, e === 0);
    else if (e < 4) fistPrint(g, add(c, s.d, e * 2), 11, true, 2);
    if (e < 6) chime(g, c, 8 + e * 3, 2, 4, fade(e, 3, 5), 1.2);
    hitStar(g, s, c, e, 'gold', 1.1);
  },
};

// ── 8. ar-buddha (ท่าพลัง): beads and the vajra body, a giant bronze palm above the foe, pressed down; terraces ripple out ──

const B_SHOW = 0.55;
const B_HIT = 1.45;
const B_R = Math.min(64, tilesPx(2.6));
const B_PALM = 20;
const bBeads = (g: G, s: Scene, front: boolean): void => {
  // The caster's beads: rising from the feet to the chest over the gather, scattering on the blow.
  if (s.t < 0.1) return;
  const e = since(s.t, B_HIT);
  if (e >= 0) {
    if (e > 8) return;
    beads(g, s.a, 16 + e * 6, 21, s.t * 4, fade(e, 4, 7), front, s.chest * 0.9 - e, 0.5);
    return;
  }
  const k = clamp01((s.t - 0.1) / 0.8);
  beads(g, s.a, 16, 21, s.t * 4, k < 0.15 ? 2 : 3, front, s.chest * 0.9 * k, 0.5);
};
const buddha: ArtFx = {
  id: 'arhat-buddha',
  skill: 'ar-buddha',
  th: 'ar-buddha',
  on: true,
  note: 'ลูกประคำลอยขึ้นวนรอบตัว ร่างเป็นทองแดง ขั้นบันไดวาดใต้เป้าบอกจุด -> ฝ่ามือสำริดยักษ์ปรากฏเหนือเป้า ค่อยชัดขึ้น -> ฟาดลง (เส้นเร็ว) ฝ่ามือกดแบนเป็นรอยบนพื้น ขั้นบันไดสามชั้นกระเพื่อมออก พื้นร้าว หินกระเด็น ลูกประคำแตกกระจาย -> ศัตรูมึน รอยฝ่ามือเย็นลง จาง',
  weapon: 'none',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 1.2 },
    { m: 'wxPalm', t0: 1.25, t1: 1.75 },
  ],
  hits: [B_HIT],
  big: B_HIT,
  ghosts: [1.28, 1.34, 1.4],
  stepIn: 8,
  cast: [0.1, 1.2],
  pre: 1.1,
  hit: B_HIT,
  end: 3.2,
  afterimage: 'port',
  beats: [
    { t: 0.1, th: 'ลูกประคำลอย' },
    { t: B_SHOW, th: 'ฝ่ามือยักษ์ปรากฏ' },
    { t: B_HIT, th: 'ฟาดลง' },
    { t: 2.2, th: 'ขั้นบันไดจาง' },
  ],
  under(g, s) {
    bBeads(g, s, false);
    // The mark: a terrace drawn under the foe over the gather.
    if (s.t >= 0.3 && s.t < B_HIT) terrace(g, s.f, Math.round(14 + clamp01((s.t - 0.3) / 0.6) * 10), s.t < 0.9 ? 2 : 3, false);
    const e = since(s.t, B_HIT);
    if (e < 0 || e > 24) return;
    const side = s.d[0] >= 0 ? 1 : -1;
    // The palm pressed flat into the ground, its print cooling.
    if (e < 18) bronzePalm(g, [s.f[0], s.f[1] + 4], B_PALM, side, 0.45, e >= 3, e < 3 ? 3 : fade(e, 10, 15));
    // Terraces stepping out (three steps, one every two frames), each settling.
    for (let k = 0; k < 3; k++) {
      const ee = e - k * 2;
      if (ee < 0 || ee > 12) continue;
      terrace(g, s.f, Math.round(B_R * (0.45 + k * 0.28) + Math.min(ee, 3) * 2), fade(ee, 6, 10), ee < 2);
    }
    cracks(g, s.f, 9, B_R * 0.9, 120, e, fade(e, 12, 18));
  },
  over(g, s) {
    // The gather: the vajra body, the sheen running up and up.
    if (s.t >= 0.1 && s.t < B_HIT) vajraBody(g, s, s.t, s.t < 0.3 ? 2 : 3, ((s.t - 0.1) / 0.35) % 1);
    bBeads(g, s, true);
    const side = s.d[0] >= 0 ? 1 : -1;
    const sky: V = [s.f[0], s.f[1] - s.chest * 1.65];
    // The giant palm: it forms over the foe, hangs, then drops in three frames.
    if (s.t >= B_SHOW && s.t < B_HIT) {
      const k = clamp01((s.t - B_SHOW) / 0.5);
      const drop = since(s.t, B_HIT - 0.25);
      const y = drop < 0 ? sky[1] - (1 - k) * 6 : sky[1] + (drop + 1) * ((s.f[1] - sky[1] - B_PALM * 0.2) / 3);
      const lv = k < 0.3 ? 1 : k < 0.65 ? 2 : 3;
      if (drop >= 0) {
        level(g, 3);
        for (let i = -3; i <= 3; i++) {
          const x = sky[0] + i * 6;
          line(g, [x, Math.max(2, y - B_PALM * 1.6 - 14)], [x, y - B_PALM * 1.3], i % 2 ? C('trim', 4) : C('gold', 6));
        }
        g.globalAlpha = 1;
      }
      bronzePalm(g, [sky[0], y], B_PALM, side, 1, false, lv);
    }
    const e = since(s.t, B_HIT);
    if (e < 0) return;
    // The blow: the palm squashing into the ground (two frames), a white flash on its lit edge.
    if (e < 2) bronzePalm(g, [s.f[0], s.f[1] - 6 + e * 4], B_PALM + 2, side, e === 0 ? 0.7 : 0.55, false, 3);
    rocks(g, s.f, e, 14, 121, 5, 5, 14);
    if (e < 6) puffs(g, s.f, e, 14, B_R * 0.4, 6, 'brown', 6, 0.6, 122);
    hitStar(g, s, strikeAt(s), e, 'gold', 1.6);
    dazed(g, s, e - 3, 14);
  },
};

/** The bell-fist moves, hand-made (each its own shape, motion and timing; one palette and motif family). */
export const ARHATFIST_FX: readonly ArtFx[] = [strike, palm, bell, sweep, brk, lion, step, buddha];
